import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { User, UserCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Admin {
  id: string;
  full_name: string;
}

interface TicketAssignmentManagerProps {
  currentAssigneeId?: string | null;
  ticketId: string;
  onAssignmentUpdate: (assigneeId: string | null) => void;
  disabled?: boolean;
}

export function TicketAssignmentManager({ 
  currentAssigneeId, 
  ticketId, 
  onAssignmentUpdate, 
  disabled 
}: TicketAssignmentManagerProps) {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string | null>(currentAssigneeId || null);
  const [loading, setLoading] = useState(true);
  const [currentAssignee, setCurrentAssignee] = useState<Admin | null>(null);

  useEffect(() => {
    fetchAdmins();
  }, []);

  useEffect(() => {
    if (currentAssigneeId && admins.length > 0) {
      const assignee = admins.find(admin => admin.id === currentAssigneeId);
      setCurrentAssignee(assignee || null);
    } else {
      setCurrentAssignee(null);
    }
  }, [currentAssigneeId, admins]);

  const fetchAdmins = async () => {
    try {
      // Primeiro, buscar IDs dos admins globais
      const { data: adminRoles, error: rolesError } = await supabase
        .from('user_global_roles')
        .select('user_id')
        .eq('role', 'portal_admin');

      if (rolesError) throw rolesError;

      if (!adminRoles || adminRoles.length === 0) {
        setAdmins([]);
        return;
      }

      // Buscar perfis dos admins
      const adminIds = adminRoles.map(r => r.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', adminIds);

      if (profilesError) throw profilesError;
      
      setAdmins(profiles || []);
    } catch (error) {
      console.error('Erro ao buscar administradores:', error);
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentChange = (assigneeId: string) => {
    if (assigneeId === 'unassigned') {
      setSelectedAssigneeId(null);
    } else {
      setSelectedAssigneeId(assigneeId);
    }
  };

  const handleUpdate = () => {
    onAssignmentUpdate(selectedAssigneeId);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return <div className="animate-pulse h-20 bg-muted rounded"></div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-medium">Responsável Atual</Label>
        <div className="mt-1">
          {currentAssignee ? (
            <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
              <Avatar className="h-6 w-6">
                <AvatarFallback className="text-xs">
                  {getInitials(currentAssignee.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{currentAssignee.full_name}</p>
              </div>
              <UserCheck className="h-4 w-4 text-primary" />
            </div>
          ) : (
            <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/10">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Não atribuído</span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <Label htmlFor="assignee-select" className="text-sm font-medium">
            Atribuir Responsável
          </Label>
          <Select
            value={selectedAssigneeId || 'unassigned'}
            onValueChange={handleAssignmentChange}
            disabled={disabled}
          >
            <SelectTrigger id="assignee-select" className="mt-1">
              <SelectValue placeholder="Selecione um responsável" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>Não atribuído</span>
                </div>
              </SelectItem>
              {admins.map((admin) => (
                <SelectItem key={admin.id} value={admin.id}>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-4 w-4">
                      <AvatarFallback className="text-xs">
                        {getInitials(admin.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm">{admin.full_name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedAssigneeId !== currentAssigneeId && (
          <Button
            onClick={handleUpdate}
            disabled={disabled}
            className="w-full"
          >
            {selectedAssigneeId ? 'Atribuir Responsável' : 'Remover Atribuição'}
          </Button>
        )}
      </div>
    </div>
  );
}